// services/habLightService.js
// HAB light settings — one row per buoy. Controls brightness + schedule (not
// color; color comes from the risk pipeline). Writes are owner-only (enforced
// server-side). See the buoy-light-settings API handoff.

const API_URL = 'https://api.subtidal.com';

const authHeaders = (token) => ({
  'Authorization': `Token ${token}`,
  'Content-Type': 'application/json',
});

// GET /hab-light-settings/?buoy=<id> → the rows for that buoy (0 or 1).
// Always resolves to a flat array: this is a DRF list endpoint, so the response
// may be a bare array OR a paginated object ({ count, next, results: [...] }).
// (Reading only `Array.isArray` here previously dropped paginated rows, so saved
// settings looked "not configured" on reload.)
export const getHabLightSettings = async (buoyId, token) => {
  const res = await fetch(`${API_URL}/hab-light-settings/?buoy=${encodeURIComponent(buoyId)}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to fetch hab light settings: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data?.results ?? []); // HabLightSetting[]
};

// POST /hab-light-settings/ — first save for a buoy. Body must include { buoy, ... }.
export const createHabLightSettings = async (payload, token) => {
  const res = await fetch(`${API_URL}/hab-light-settings/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to create hab light settings: ${res.status}`);
  return res.json();
};

// PATCH /hab-light-settings/<id>/ — partial update of an existing row.
export const updateHabLightSettings = async (id, patch, token) => {
  const res = await fetch(`${API_URL}/hab-light-settings/${id}/`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to update hab light settings: ${res.status}`);
  return res.json();
};

// DELETE /hab-light-settings/<id>/ — optional (e.g. reset to default).
export const deleteHabLightSettings = async (id, token) => {
  const res = await fetch(`${API_URL}/hab-light-settings/${id}/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to delete hab light settings: ${res.status}`);
  return true; // 204 No Content
};
